import React, { memo, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import designSystemColors from "../../styles";
import { HourlyPeak } from "../../hooks/useOperationPeaks/types";

type OperationPeaksChartProps = {
  hourly: HourlyPeak[];
};

const getYAxisMax = (hourly: HourlyPeak[]) => {
  const maxCount = Math.max(...hourly.map((item) => item.count), 0);

  if (maxCount === 0) {
    return 50;
  }

  return Math.ceil(maxCount / 50) * 50;
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: HourlyPeak }>;
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E8E8E9",
        borderRadius: 8,
        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.15)",
        padding: "12px 16px",
        fontSize: 13,
      }}
    >
      <strong>Análise Horária</strong>
      <div style={{ marginTop: 4 }}>
        {item.hour}h-{item.hour + 1}h:{" "}
        <span style={{ color: designSystemColors.infoColor }}>
          {item.count.toLocaleString("pt-BR")} Atendimentos Iniciados
        </span>
      </div>
    </div>
  );
};

function OperationPeaksChart({ hourly }: OperationPeaksChartProps) {
  const yAxisMax = useMemo(() => getYAxisMax(hourly), [hourly]);
  const yAxisTicks = useMemo(
    () => Array.from({ length: yAxisMax / 50 + 1 }, (_, index) => index * 50),
    [yAxisMax]
  );

  return (
    <div data-testid="operation-peaks-chart">
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={hourly} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E9" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: designSystemColors.grayChart }}
          axisLine={{ stroke: "#D9D9D9" }}
          tickLine={false}
          label={{
            value: "Horário Operacional",
            position: "insideBottom",
            offset: -4,
            style: { fontSize: 12, fill: designSystemColors.grayTitle },
          }}
        />
        <YAxis
          domain={[0, yAxisMax]}
          ticks={yAxisTicks}
          tick={{ fontSize: 11, fill: designSystemColors.grayChart }}
          axisLine={false}
          tickLine={false}
          label={{
            value: "Total Atendimentos",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 12, fill: designSystemColors.grayTitle },
          }}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(81, 136, 244, 0.08)" }} />
        <Bar
          dataKey="count"
          fill={designSystemColors.infoColor}
          radius={[6, 6, 0, 0]}
          maxBarSize={28}
        >
          <LabelList
            dataKey="count"
            position="top"
            style={{ fontSize: 11, fill: designSystemColors.grayTitle }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
    </div>
  );
}

export default memo(OperationPeaksChart);
